import {
	createEffect,
	createMemo,
	createSignal,
	type JSX,
	onCleanup,
	untrack,
} from "solid-js";
import { formatMarkdown } from "~/modules/shared/utils/text";
import { Mention } from "../../components/Mention.tsx";
import { VideoMention } from "../../components/VideoMention.tsx";
import {
	createIncrementalMarkdownView,
	type MarkdownChunkHtml,
} from "../../utils/incrementalMarkdownView.tsx";
import {
	findPlaceholderPositions,
	markdownForSpeakingSegment,
} from "../../utils/parseSpeakingBlockSegments.ts";
import {
	type PlayVideoMentionsHandler,
	videoMentionsInBlockOrder,
} from "../../utils/videoMentionPlayback.ts";
import type { SpeakingBlockState } from "../chatPanelState.ts";

declare const pageVars: {
	appEmbedded: boolean;
};

type Props = {
	block: SpeakingBlockState;
	segmentKey: string;
	interactionId: string;
	idBase: string;
	onMentionedVideosDomUpdated?: () => void;
	onPlayVideoMentions?: PlayVideoMentionsHandler;
};

/** Markdown run with inline mention/video placeholders hydrated into the DOM. */
export const SpeakingMarkdownSegment = (props: Props) => {
	const [container, setContainer] = createSignal<HTMLElement | undefined>();
	// Every segment of a block reads the block's whole markdown, so without
	// this each token would re-render the segments before it too.
	const segmentMarkdown = createMemo(() =>
		markdownForSpeakingSegment(props.block, props.segmentKey),
	);

	const toHtml = (markdown: string): MarkdownChunkHtml => {
		const block = props.block;
		const placeholders = new Map<string, () => JSX.Element>();
		const inlinePlaceholderIds = Object.entries(block.mentions)
			.filter(([, entry]) => entry.kind !== "artifact")
			.map(([id]) => id)
			.filter((id) => markdown.includes(id));

		let html = formatMarkdown(markdown);
		const placeholderPositions = findPlaceholderPositions(
			html,
			inlinePlaceholderIds,
		);

		for (const position of [...placeholderPositions].reverse()) {
			const entry = block.mentions[position.id];
			if (!entry || entry.kind === "artifact") {
				continue;
			}

			html =
				html.slice(0, position.index) +
				`<span data-mention-placeholder="${position.id}"></span>` +
				html.slice(position.index + position.length);

			if (entry.kind === "mention") {
				placeholders.set(position.id, () => (
					<Mention mention={entry.data} appEmbedded={pageVars.appEmbedded} />
				));
			} else {
				placeholders.set(position.id, () => (
					<VideoMention
						mention={entry.data}
						// Read when clicked, so a mention rendered early still plays
						// through the clips cited after it.
						allVideoMentions={videoMentionsInBlockOrder(props.block)}
						appEmbedded={pageVars.appEmbedded}
						interactionId={props.interactionId}
						onPlayVideoMentions={props.onPlayVideoMentions}
					/>
				));
			}
		}

		return { html, placeholders };
	};

	let view: ReturnType<typeof createIncrementalMarkdownView> | undefined;

	createEffect(() => {
		const el = container();
		const text = segmentMarkdown();
		if (!el || text.length === 0) {
			return;
		}

		view ??= createIncrementalMarkdownView(el, {
			tableIdBase: `${props.idBase}-${props.segmentKey}`,
			theme: "borderless",
			allowFullWidth: !pageVars.appEmbedded,
			toHtml,
		});
		untrack(() => view?.update(text));

		props.onMentionedVideosDomUpdated?.();
	});

	onCleanup(() => view?.dispose());

	return <div ref={setContainer} class="speaking-markdown-segment" />;
};
